/*
 * VoxPontLogo — the live VoxPont brand mark for the React app.
 *
 * Renders the exact same vector geometry as the exported asset package by
 * building the SVG from the shared source of truth
 * (voxpontLogoGeometry.mjs) and inlining it. Inlining (rather than an <img>)
 * keeps the gold/blue gradients crisp at any size and lets the mark inherit
 * the surrounding layout.
 *
 * Coupling: scripts/build-voxpont-assets.mjs builds the downloadable files from
 * the same module, so the live logo and the brand package never drift.
 */

import React, { useId, useMemo } from "react";
import PropTypes from "prop-types";

import { buildVoxPontSvg } from "./voxpontLogoGeometry.mjs";

// Friendly prop names -> geometry variant keys.
const VARIANT_MAP = {
  full: "primary",
  "full-no-tagline": "primary-no-tagline",
  symbol: "symbol",
  wordmark: "wordmark",
  "wordmark-tagline": "wordmark-tagline",
  stacked: "stacked",
};

/**
 * @param {Object} props
 * @param {('full'|'full-no-tagline'|'symbol'|'wordmark'|'wordmark-tagline'|'stacked')} [props.variant]
 * @param {('color'|'onDark'|'white'|'blue'|'black'|'gold')} [props.tone]
 * @param {number|string} [props.height] - rendered height (px number or CSS length). Width auto-scales.
 * @param {string} [props.title] - accessible label.
 * @param {string} [props.className]
 * @param {object} [props.style]
 */
function VoxPontLogo({
  variant = "full",
  tone = "color",
  height,
  title = "VoxPont",
  className,
  style,
}) {
  const rawId = useId();
  const idPrefix = `vpl${rawId.replace(/[^a-zA-Z0-9]/g, "")}`;

  const geomVariant = VARIANT_MAP[variant] || "primary";
  const geomTone = tone === "onDark" ? "ondark" : tone;

  const html = useMemo(() => {
    const svg = buildVoxPontSvg({
      variant: geomVariant,
      tone: geomTone,
      idPrefix,
    });
    const sized =
      height != null
        ? `height:${typeof height === "number" ? `${height}px` : height};width:auto`
        : "width:100%;height:auto";
    return svg.replace("<svg ", `<svg style="display:block;${sized}" `);
  }, [geomVariant, geomTone, idPrefix, height]);

  return (
    <span
      className={className}
      role="img"
      aria-label={title}
      style={{ display: "inline-flex", lineHeight: 0, ...style }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

VoxPontLogo.propTypes = {
  variant: PropTypes.oneOf([
    "full",
    "full-no-tagline",
    "symbol",
    "wordmark",
    "wordmark-tagline",
    "stacked",
  ]),
  tone: PropTypes.oneOf(["color", "onDark", "white", "blue", "black", "gold"]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default VoxPontLogo;
